import React, { useState, useCallback } from 'react'
import { Row, Button, H3, Overline, Subheading, InfoSheetPanel, useOverlay, Separator, usePageWidth } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import ZionForm, { useZionForm, FormAutosuggest } from '@fs/zion-form'
import {
  getImage,
  getNestedOrFloatingElementById,
  getNextArkId,
  getPersonName,
  getPersonRelationships,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { savePrimaryPerson, saveNotPrimaryPerson } from '../../utils/saveFunctions'
import { saveNewPerson } from '../../utils/savePersonFunctions'
import { stepsMap } from '../../providers/FamilyReviewReducer'
import { useFamilyReviewDispatchContext, useFamilyReviewStateContext, STEPS } from '../../providers/FamilyReviewContext'
import { getPrimaryOrFirstPerson } from '../../utils/utils'
import { showEssentialInfoHighlights } from '../../utils/highlightUtils'
import { getNextUuid } from '../../../../../shared/utils/utils'

import CreateNewPersonFormPage1 from '../createNewPerson/CreateNewPersonFormPage1'
import CreateNewPersonFormPage2 from '../createNewPerson/CreateNewPersonFormPage2'
import useFamilyReviewRelationshipOptions from '../../hooks/useFamilyReviewRelationshipOptions'
import CrossRecordWarningOverlay from '../../../indexing/components/editPanels/CrossRecordWarningOverlay'
import RecordTooltip from './RecordTooltip'
import HelpWithThisStep from '../faqContent/HelpWithThisStep'
import RecordTooltipButton from './RecordTooltipButton'

export default function StepOnePartTwo({
  addNewPersonOverlay,
  currentTask,
  stepOnePartTwoOverlay,
  stepTwoOverlay,
  setSaveUpdateCalled,
  editHighlightsOverlay,
  hasImagePermission,
  handleGoToNextRecordAndResetStep,
}) {
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const saveChangesFlag = useFeatureFlag('rip_sharedImageViewer_saveChanges')
  const zionSlsDispatch = useSlsDispatchContext()
  const zionSlsState = useSlsStateContext()
  const state = useFamilyReviewStateContext()
  const dispatch = useFamilyReviewDispatchContext()
  const currentImage = getImage(zionSlsState.images, zionSlsState.currentImageArk)

  const [nameType, setNameType] = useState('BIRTH_NAME')
  const [givenField, setGivenField] = useState('')
  const [surnameField, setSurnameField] = useState('')
  const [givenNameFieldId, setGivenNameFieldId] = useState()
  const [surnameFieldId, setSurnameFieldId] = useState()
  const [crossRecordPerson, setCrossRecordPerson] = useState()
  const [crossRecordRelationships, setCrossRecordRelationships] = useState([])
  const [personId, setPersonId] = useState('')
  const [inputText, setInputText] = useState('')

  const methods = useZionForm({
    mode: 'all',
    defaultValues: {
      selectedId: { key: '', primaryText: '' },
    },
    resolver: (values) => {
      const errors = {}
      methods.clearErrors()
      const { selectedId } = values
      if (!selectedId || !selectedId.key)
        errors.selectedId = { type: 'required', message: t('FormValidation.notEmpty', 'Field cannot be empty.') }
      return { values, errors }
    },
  })

  const resetPersonId = useCallback(async () => {
    const id = await getNextArkId('person')
    setPersonId(id)
    dispatch({
      type: 'SET_SELECTED_PERSON_ID',
      value: id,
    })
  }, [dispatch])

  const createNewPersonFormPage1Overlay = useOverlay()
  const createNewPersonFormPage2Overlay = useOverlay()
  const crossRecordOverlay = useOverlay()
  const primaryPerson = getPrimaryOrFirstPerson(currentTask)
  const primaryPersonName = getPersonName({ person: primaryPerson })
  const autocompleteSuggestions = useFamilyReviewRelationshipOptions({
    images: zionSlsState.images,
    mainPartitionKey: currentTask.partitionKey,
    splitScreenPartitionKey: state.splitScreenPartitionKey,
    primaryPersonId: primaryPerson?.id,
    currentRecordId: currentTask?.id,
  })

  const hasSelectedHighlights = useCallback(
    (fieldId) => {
      return [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights].find(
        (highlight) => highlight.fieldId === fieldId
      )
    },
    [h]
  )
  const resetStateAndHighlights = () => {
    if (state.currentStep === stepsMap.STEP_ONE) {
      const primaryPersonId = primaryPerson?.id || currentTask?.subElements?.[0]?.id
      showEssentialInfoHighlights({
        h,
        mainPartitionKey: currentTask.partitionKey,
        splitScreenPartitionKey: state.splitScreenPartitionKey,
        personIds: [primaryPersonId],
        isSmallScreen,
      })
      dispatch({ type: 'SET_SELECTED_PERSON_ID', value: primaryPersonId })
    } else if (state.currentStep === stepsMap.STEP_TWO) {
      const currentRelationshipCardIndex = state.stepTwo.relationshipCards.findIndex(
        (cardState) => !cardState.hasBeenReviewed
      )
      dispatch({
        type: 'SET_SELECTED_PERSON_ID',
        value: state.stepTwo.relationshipCards[currentRelationshipCardIndex].personId,
      })
      const nextPersonCard = state.stepTwo.relationshipCards[currentRelationshipCardIndex + 1]
      if (nextPersonCard) {
        showEssentialInfoHighlights({
          h,
          mainPartitionKey: currentTask.partitionKey,
          splitScreenPartitionKey: state.splitScreenPartitionKey,
          personIds: [nextPersonCard.personId],
          isSmallScreen,
        })
      }
    }
  }

  const userClosedPanel = () => {
    methods.reset({ selectedId: { key: '', primaryText: '' } })
    stepOnePartTwoOverlay.close()
    addNewPersonOverlay.close()
    resetStateAndHighlights()
  }

  const saveButtonClicked = async () => {
    const checked = methods.getValues()
    // magic sauce to filter the checked object to an array of true values only
    // (i.e. {1:1:6MXT-TNTH-FCLQ-Y: false, 1:1:6MXT-TNTH-FCLQ-5: true} => ['1:1:6MXT-TNTH-FCLQ-5'])
    // const primaryPersons = Object.keys(Object.fromEntries(Object.entries(checked).filter(([, value]) => value)))
    const primaryPersonId = Object.values(Object.fromEntries(Object.entries(checked).filter(([, value]) => value)))[0]
      .key
    const currentPrimaries = currentTask.subElements
      .filter((p) => p.elementType === 'PERSON' && p.primary)
      .map((p) => p?.id)
    if (!currentPrimaries.includes(primaryPersonId) || !state.stepTwo.initialized) {
      await savePrimaryPerson({
        primaryPerson: getNestedOrFloatingElementById(zionSlsState.images, primaryPersonId),
        currentTask,
        partitionKey: currentTask.partitionKey,
        zionSLSState: zionSlsState,
        zionSlsDispatch,
        userId: state.userId,
        saveChangesFlag,
      })
      const notPrimaries = currentPrimaries.filter((id) => id !== primaryPersonId)
      await saveNotPrimaryPerson({
        notPrimaries,
        partitionKey: currentTask.partitionKey,
        zionSLSState: zionSlsState,
        zionSlsDispatch,
        userId: state.userId,
        saveChangesFlag,
      })
      setSaveUpdateCalled(true)
    }
    methods.reset({ selectedId: { key: '', primaryText: '' } })
    stepOnePartTwoOverlay.close()
    stepTwoOverlay.open()
    dispatch({ type: 'SET_CURRENT_STEP', value: STEPS.STEP_TWO })
  }

  const cancelButtonClicked = () => {
    methods.reset({ selectedId: { key: '', primaryText: '' } })
    stepOnePartTwoOverlay.close()
    resetStateAndHighlights()
  }

  const handleSubmitCreateForm1 = (values, givenNameFieldIdTemp, surnameFieldIdTemp) => {
    setNameType(values.nameType)
    setGivenField(values.givenField)
    setSurnameField(values.surnameField)
    setGivenNameFieldId(givenNameFieldIdTemp)
    setSurnameFieldId(surnameFieldIdTemp)
    const addedSexId = getNextUuid()
    dispatch({ type: 'SET_STATE', value: { addedSexId } })
    createNewPersonFormPage2Overlay.handleClick()
  }

  const handleSubmitCreateForm2 = async (values, sexFieldId) => {
    await saveNewPerson({
      partitionKey: zionSlsState.currentImageArk,
      addNewPersonData: {
        nameType,
        givenField,
        surnameField,
        sexField: values.sexField,
        relationshipField: values.relationshipField,
        givenNameFieldId,
        surnameFieldId,
        sexFieldId,
      },
      personId,
      userId: state.userId,
      recordId: primaryPerson.recordId,
      setAsPrimary: true,
      highlights: [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights],
      relatedPersonId: primaryPerson?.id,
      zionSLSState: zionSlsState,
      zionSlsDispatch,
      currentTask,
      saveChangesFlag,
    })
    setSaveUpdateCalled(true)
    createNewPersonFormPage2Overlay.close()
    createNewPersonFormPage1Overlay.close()
    addNewPersonOverlay.close()
    resetStateAndHighlights()
  }

  const handleOnCancelCrossRecordWarning = () => {
    methods.reset({ selectedId: { key: '', primaryText: '' } })
    h.clearDisplay({ all: true })
  }
  const recordTooltipOverlay = useOverlay()

  return (
    <>
      <ZionForm methods={methods} onSubmit={saveButtonClicked} id="step-one-part-two-form">
        <InfoSheetPanel
          {...{ ...stepOnePartTwoOverlay, close: userClosedPanel }}
          headingColor="purple"
          title={t('family-review.step1.heading', 'Identify Primary Person')}
          footer={
            <Row alignX="end">
              <Button
                onClick={cancelButtonClicked}
                linkName="Step One Part Two Cancel"
                data-testid="step-one-part-two-cancel-button"
              >
                {t('edit-index.cancel.button', 'Cancel')}
              </Button>
              <Button
                emphasis="high"
                linkName="Step One Part Two Save"
                data-testid="step-one-part-two-save-button"
                form="step-one-part-two-form"
                type="submit"
              >
                {t('edit-index.save.button', 'Save')}
              </Button>
            </Row>
          }
        >
          {stepOnePartTwoOverlay.isOpen && (
            <>
              <H3 size="H5">
                <Overline>{t('family-review.stepXof4', 'Step {step} of 4', { step: '1' })}</Overline>
                {t('family-review.step1.part2.header-q', 'Who is this record primarily about?')}{' '}
                <RecordTooltipButton overlay={recordTooltipOverlay} stepText="One Part Two" />
                <Subheading>
                  {t(
                    'family-review.step1.part2.content-subhead',
                    'The primary person is who this record was created about.'
                  )}
                </Subheading>
              </H3>
              <RecordTooltip overlay={recordTooltipOverlay} />
              <Separator size="sm" />
              <FormAutosuggest
                data-testid="select-primary-person-autosuggest"
                clearable
                clearOnClickAway
                label={t('family-review.step1.part2.select-primary-person', 'Select primary person')}
                suggestions={autocompleteSuggestions}
                name="selectedId"
                control={methods.control}
                placeholder={t('family-review.step1.part2.select-primary-person', 'Select primary person')}
                lastResort={{
                  key: 'LAST-RESORT-KEY',
                  primaryText: t('family-review.addPerson.name-not-on-list', 'Name Not In This List'),
                  onClick: () => {
                    createNewPersonFormPage1Overlay.handleClick()
                    resetPersonId()
                    methods.reset({ selectedId: { key: '', primaryText: '' } }) // Reset this whenever overlay opens
                    setInputText('')
                  },
                }}
                inputText={inputText}
                onInputTextChange={setInputText}
                onSuggestionSelected={(suggestion) => {
                  if (!createNewPersonFormPage1Overlay.isOpen && suggestion) {
                    const selectedPerson = getNestedOrFloatingElementById([currentImage], suggestion?.key)
                    dispatch({ type: 'SET_SELECTED_PERSON_ID', value: suggestion?.key })
                    if (selectedPerson?.recordId !== primaryPerson?.recordId) {
                      const relationships = getPersonRelationships(selectedPerson, [currentImage])
                      if (relationships?.length > 0) {
                        setCrossRecordPerson(selectedPerson)
                        setCrossRecordRelationships(relationships)
                        crossRecordOverlay.open()
                      }
                    }
                    showEssentialInfoHighlights({
                      h,
                      mainPartitionKey: currentTask.partitionKey,
                      splitScreenPartitionKey: state.splitScreenPartitionKey,
                      personIds: [suggestion?.key],
                      isSmallScreen,
                    })
                  }
                }}
              />
              <HelpWithThisStep
                currentStep="1-2"
                hasImagePermission={hasImagePermission}
                handleGoToNextRecordAndResetStep={handleGoToNextRecordAndResetStep}
                personId={primaryPerson?.id}
              />
              {crossRecordPerson && (
                <CrossRecordWarningOverlay
                  overlay={crossRecordOverlay}
                  person={crossRecordPerson}
                  existingRelationships={crossRecordRelationships}
                  genericVersion
                  onCancel={handleOnCancelCrossRecordWarning}
                />
              )}
            </>
          )}
        </InfoSheetPanel>
      </ZionForm>
      <CreateNewPersonFormPage1
        createNewPersonFormPage1Overlay={createNewPersonFormPage1Overlay}
        currentTask={currentTask}
        handleSubmit={handleSubmitCreateForm1}
        hasSelectedHighlights={hasSelectedHighlights}
        editHighlightsOverlay={editHighlightsOverlay}
        headingColor="purple"
        isPrimaryPerson
      />
      <CreateNewPersonFormPage2
        editHighlightsOverlay={editHighlightsOverlay}
        createNewPersonFormPage1Overlay={createNewPersonFormPage1Overlay}
        createNewPersonFormPage2Overlay={createNewPersonFormPage2Overlay}
        stepOnePartTwoOverlay={stepOnePartTwoOverlay}
        givenField={givenField}
        surnameField={surnameField}
        primaryPersonName={primaryPersonName}
        handleSubmit={handleSubmitCreateForm2}
        headingColor="purple"
        isPrimaryPerson
        stepTwoOverlay={stepTwoOverlay}
      />
    </>
  )
}
